/**
 * V4OrganizationUsers — the in-shell (/v4/admin/users) users surface a platform
 * admin reaches via "View Users" on an organization row.
 *
 * Aligned with the org-onboarding flow: adding a user takes Email + Full Name +
 * Role and returns a magic-link the admin copies (no username, no typed
 * password — email is the login identifier). The table shows Name / Email /
 * Role (the RBAC role) / Actions.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

import { fetchWithAuth } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useNotice } from '../components/v3/shared/NoticeProvider';
import Modal from '../components/Modal';

interface OrgUser {
  user_id: string;
  name: string | null;
  email: string;
  role: string; // legacy role (back-compat)
  rbac_role: string | null; // primary RBAC role key
}

interface Organization {
  organization_id: string;
  name: string;
}

// Only the two team-independent roles can be added without a team (matches the
// onboarding flow + the backend restriction). Team-scoped members are added
// from the org's own RBAC roles screen.
const ADD_USER_ROLES = [
  { key: 'FIRM_ADMIN', label: 'Organization Admin' },
  { key: 'VIEWER', label: 'Viewer' },
];

// Friendly labels for any RBAC role key we might display.
const ROLE_LABELS: Record<string, string> = {
  FIRM_ADMIN: 'Organization Admin',
  VIEWER: 'Viewer',
  END_USER: 'User',
  TEAM_LEAD: 'Team Lead',
  PLATFORM_ADMIN: 'Platform Admin',
};

const roleLabel = (u: OrgUser): string =>
  (u.rbac_role && (ROLE_LABELS[u.rbac_role] ?? u.rbac_role)) || '—';

interface SetupResult {
  email: string;
  setupUrl: string;
  emailSent: boolean;
}

const V4OrganizationUsers: React.FC = () => {
  // Prefer the query param so a refresh / bookmark / direct link still works;
  // fall back to router state for older links that only passed it that way.
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const orgId: string | undefined =
    searchParams.get('org') ||
    (location.state as { orgId?: string } | null)?.orgId ||
    undefined;
  const { user: currentUser } = useAuth();
  const { show: showNotice } = useNotice();

  const [org, setOrg] = useState<Organization | null>(null);
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add-user modal state
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', role: 'FIRM_ADMIN' });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [setupResult, setSetupResult] = useState<SetupResult | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!orgId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [orgs, userList] = await Promise.all([
        fetchWithAuth('/v1/auth/admin/organizations') as Promise<Organization[]>,
        fetchWithAuth(`/v1/auth/admin/users?organization_id=${orgId}`) as Promise<OrgUser[]>,
      ]);
      setOrg(orgs.find((o) => o.organization_id === orgId) ?? null);
      setUsers(userList);
      setError(null);
    } catch (err) {
      setError('Failed to load users. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setForm({ email: '', name: '', role: 'FIRM_ADMIN' });
    setFormError(null);
    setSetupResult(null);
    setCopied(false);
    setIsAddOpen(true);
  };

  const closeAdd = () => {
    const created = !!setupResult;
    setIsAddOpen(false);
    setSetupResult(null);
    setCopied(false);
    if (created) load(); // refresh the list once the admin is done with the link
  };

  const submitAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const result = await fetchWithAuth(`/v1/auth/admin/organizations/${orgId}/users`, {
        method: 'POST',
        body: JSON.stringify({
          email: form.email.trim(),
          name: form.name.trim() || undefined,
          role: form.role,
        }),
      });
      setSetupResult({
        email: result.user_email,
        setupUrl: result.setup_url,
        // A response without the field reads as not sent.
        emailSent: result.email_sent === true,
      });
      setCopied(false);
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : null;
      setFormError(message || 'Could not add the user. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const copySetupUrl = async () => {
    if (!setupResult) return;
    try {
      await navigator.clipboard.writeText(setupResult.setupUrl);
      setCopied(true);
    } catch {
      showNotice({ type: 'error', message: 'Could not copy — select and copy the link manually.' });
    }
  };

  const handleDelete = async (u: OrgUser) => {
    if (u.user_id === currentUser?.user_id) return; // can't delete yourself
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await fetchWithAuth(`/v1/auth/admin/users/${u.user_id}`, { method: 'DELETE' });
      setUsers((prev) => prev.filter((x) => x.user_id !== u.user_id));
      showNotice({ type: 'success', message: 'User deleted', autoDismissMs: 3000 });
    } catch (err) {
      showNotice({ type: 'error', message: 'Failed to delete user. Please try again.' });
      console.error(err);
    }
  };

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="bg-white shadow-sm rounded-lg p-6 max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-xl font-bold text-foreground">Users</h1>
            {org && <p className="text-sm text-gray-500 mt-0.5">{org.name}</p>}
          </div>
          {orgId && (
            <button
              onClick={openAdd}
              className="px-4 py-2 bg-primary text-secondary rounded-md flex items-center hover:bg-primary/90 transition-colors"
            >
              <Plus className="size-4 mr-2" /> Add user
            </button>
          )}
        </div>

        {!orgId ? (
          <div className="text-center text-gray-500 py-10">
            Open this page from an organization's <span className="font-medium">View Users</span> action.
          </div>
        ) : loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
          </div>
        ) : error ? (
          <div className="bg-red-50 p-4 rounded-lg text-red-800">
            <p>{error}</p>
            <button
              onClick={load}
              className="mt-2 px-4 py-2 bg-red-100 hover:bg-red-200 rounded-md text-red-800"
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="text-left border-b-2 border-gray-200">
                  <th className="px-4 py-3 text-sm font-semibold text-gray-600">Name</th>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-600">Email</th>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-600">Role</th>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-4 text-center text-gray-500">
                      No users yet
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-900">
                        {u.name || <em className="text-gray-400">—</em>}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{u.email}</td>
                      <td className="px-4 py-3 text-gray-600">{roleLabel(u)}</td>
                      <td className="px-4 py-3">
                        <button
                          className="p-1 rounded hover:bg-red-100 text-red-600 transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                          onClick={() => handleDelete(u)}
                          disabled={u.user_id === currentUser?.user_id}
                          title={
                            u.user_id === currentUser?.user_id
                              ? "You can't delete your own account"
                              : 'Delete user'
                          }
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add-user modal — Email + Full Name + Role, then the setup link. */}
      <Modal isOpen={isAddOpen} onClose={closeAdd} title={setupResult ? 'User added' : 'Add user'}>
        {setupResult && (
          <p className="text-sm text-gray-700">
            <span className="font-medium">{setupResult.email}</span> was added
            to <span className="font-medium">{org?.name ?? 'the organization'}</span>.
          </p>
        )}
        {/* Mounted with the form, before the result, so a screen reader announces the line it fills. */}
        <div role="status" className={setupResult ? 'mt-4' : undefined}>
          {setupResult &&
            (setupResult.emailSent ? (
              <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                <span>We've emailed them a link to set their password.</span>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                <span>The email didn't send. Copy the setup link and send it to them yourself.</span>
              </div>
            ))}
        </div>
        {setupResult ? (
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Setup link</label>
              <div className="flex items-stretch gap-2">
                <input
                  type="text"
                  readOnly
                  value={setupResult.setupUrl}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 p-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-700"
                />
                <button
                  type="button"
                  onClick={copySetupUrl}
                  className="px-4 py-2 bg-primary text-secondary rounded-md hover:bg-primary/90 whitespace-nowrap"
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                This link lets them set their own password. It expires in 24 hours.
              </p>
            </div>
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={closeAdd}
                className="px-4 py-2 bg-primary text-secondary rounded-md hover:bg-primary/90"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submitAdd} className="space-y-4">
            {formError && (
              <div className="bg-red-50 p-4 rounded-lg text-red-800">{formError}</div>
            )}
            <div>
              <label htmlFor="add-user-email" className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                id="add-user-email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary"
                required
              />
            </div>
            <div>
              <label htmlFor="add-user-name" className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                id="add-user-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>
            <div>
              <label htmlFor="add-user-role" className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                id="add-user-role"
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary"
              >
                {ADD_USER_ROLES.map((r) => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={closeAdd}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-secondary rounded-md hover:bg-primary/90 disabled:opacity-60"
                disabled={submitting}
              >
                {submitting ? 'Adding…' : 'Add user'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default V4OrganizationUsers;
